import { useMutation, useQuery } from '@apollo/client/react/index.js';
import {
  AnimatedCounter,
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassConfirmDialog,
  GlassEmptyState,
  GlassInput,
  GlassSelect,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
  SkeletonLoader,
} from '@merritrac/shared/components';
import {
  Contact,
  Mail,
  Pencil,
  Phone,
  Plus,
  Search,
  Trash2,
  UserCheck,
  UserCog,
  Users,
  X,
} from 'lucide-react';
import { useState } from 'react';

import {
  CREATE_UNIT_CONTACT,
  DELETE_UNIT_CONTACT,
  GET_UNIT_CONTACTS,
  UPDATE_UNIT_CONTACT,
} from '../lib/graphql/operations.js';

import type { ReactElement } from 'react';

/* ── GraphQL response types ── */

interface PropertyRef {
  id: number;
  name: string;
}

interface UnitContactNode {
  id: number;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  contactType: string;
  propertyId: number | null;
  unitAk: number | null;
  active: number | null;
  property: PropertyRef | null;
}

interface UnitContactEdge {
  node: UnitContactNode;
}

interface UnitContactConnection {
  totalCount: number;
  edges: UnitContactEdge[];
}

interface GetUnitContactsData {
  unitContacts: UnitContactConnection;
}

/* ── Constants ── */

type ContactTypeFilter = 'all' | 'property_manager' | 'unit_owner' | 'resident';

const contactTypeOptions: { value: ContactTypeFilter; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'property_manager', label: 'Property Manager' },
  { value: 'unit_owner', label: 'Unit Owner' },
  { value: 'resident', label: 'Resident' },
];

function contactTypeBadgeVariant(contactType: string): 'info' | 'warning' | 'success' | 'default' {
  switch (contactType) {
    case 'property_manager':
      return 'info';
    case 'unit_owner':
      return 'warning';
    case 'resident':
      return 'success';
    default:
      return 'default';
  }
}

function formatContactType(contactType: string): string {
  return contactType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function fullName(contact: UnitContactNode): string {
  return [contact.firstName, contact.lastName].filter(Boolean).join(' ');
}

/* ── Blank form state ── */

interface ContactForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  contactType: string;
  active: number;
}

const emptyForm: ContactForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  contactType: 'resident',
  active: 1,
};

/* ── Inline form component ── */

function ContactFormPanel({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial: ContactForm;
  onSave: (form: ContactForm) => void;
  onCancel: () => void;
  saving: boolean;
}): ReactElement {
  const [form, setForm] = useState<ContactForm>(initial);

  function set<K extends keyof ContactForm>(key: K, value: ContactForm[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <div className="border-t border-glass-border bg-glass-subtle px-5 py-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">
            First Name <span className="text-danger">*</span>
          </label>
          <GlassInput
            placeholder="First name"
            value={form.firstName}
            onChange={(e) => {
              set('firstName', e.target.value);
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">
            Last Name <span className="text-danger">*</span>
          </label>
          <GlassInput
            placeholder="Last name"
            value={form.lastName}
            onChange={(e) => {
              set('lastName', e.target.value);
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Email</label>
          <GlassInput
            type="email"
            placeholder="email@example.com"
            value={form.email}
            onChange={(e) => {
              set('email', e.target.value);
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Phone</label>
          <GlassInput
            type="tel"
            placeholder="(555) 000-0000"
            value={form.phone}
            onChange={(e) => {
              set('phone', e.target.value);
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">
            Contact Type <span className="text-danger">*</span>
          </label>
          <GlassSelect
            value={form.contactType}
            onChange={(e) => {
              set('contactType', e.target.value);
            }}
            options={[
              { value: 'property_manager', label: 'Property Manager' },
              { value: 'unit_owner', label: 'Unit Owner' },
              { value: 'resident', label: 'Resident' },
            ]}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Status</label>
          <GlassSelect
            value={String(form.active)}
            onChange={(e) => {
              set('active', Number(e.target.value));
            }}
            options={[
              { value: '1', label: 'Active' },
              { value: '0', label: 'Inactive' },
            ]}
          />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <GlassButton
          variant="primary"
          size="sm"
          disabled={form.firstName.trim() === '' || form.lastName.trim() === '' || saving}
          onClick={() => {
            onSave(form);
          }}
        >
          {saving ? 'Saving…' : 'Save Contact'}
        </GlassButton>
        <GlassButton variant="ghost" size="sm" onClick={onCancel}>
          <X size={14} />
          Cancel
        </GlassButton>
      </div>
    </div>
  );
}

/* ── Delete dialog ── */

function DeleteContactDialog({
  contact,
  open,
  onClose,
  onDeleted,
}: {
  contact: UnitContactNode | null;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}): ReactElement {
  const [deleteContact, { loading }] = useMutation(DELETE_UNIT_CONTACT, {
    refetchQueries: [{ query: GET_UNIT_CONTACTS, variables: { first: 200 } }],
  });

  return (
    <GlassConfirmDialog
      open={open}
      title="Delete Contact"
      message={
        contact !== null
          ? `Are you sure you want to delete "${fullName(contact)}"? This action cannot be undone.`
          : ''
      }
      confirmLabel="Delete Contact"
      variant="danger"
      loading={loading}
      onCancel={onClose}
      onConfirm={() => {
        if (contact === null) {
          return;
        }
        void deleteContact({ variables: { id: contact.id } }).then(() => {
          onDeleted();
        });
      }}
    />
  );
}

/* ── Page ── */

export function ContactsPage(): ReactElement {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<ContactTypeFilter>('all');

  // Inline form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingContact, setEditingContact] = useState<UnitContactNode | null>(null);

  // Delete dialog state
  const [deleteTarget, setDeleteTarget] = useState<UnitContactNode | null>(null);

  const { data, loading, error } = useQuery<GetUnitContactsData>(GET_UNIT_CONTACTS, {
    variables: {
      first: 200,
      ...(typeFilter !== 'all' ? { contactType: typeFilter } : {}),
    },
  });

  const [createContact, { loading: creating }] = useMutation(CREATE_UNIT_CONTACT, {
    refetchQueries: [
      { query: GET_UNIT_CONTACTS, variables: { first: 200 } },
      {
        query: GET_UNIT_CONTACTS,
        variables: {
          first: 200,
          ...(typeFilter !== 'all' ? { contactType: typeFilter } : {}),
        },
      },
    ],
  });

  const [updateContact, { loading: updating }] = useMutation(UPDATE_UNIT_CONTACT, {
    refetchQueries: [
      { query: GET_UNIT_CONTACTS, variables: { first: 200 } },
      {
        query: GET_UNIT_CONTACTS,
        variables: {
          first: 200,
          ...(typeFilter !== 'all' ? { contactType: typeFilter } : {}),
        },
      },
    ],
  });

  const contacts: UnitContactNode[] = data?.unitContacts.edges.map((e) => e.node) ?? [];

  const filtered = contacts.filter((c) => {
    const query = search.toLowerCase().trim();
    if (query === '') {
      return true;
    }
    const name = fullName(c).toLowerCase();
    const email = (c.email ?? '').toLowerCase();
    const phone = (c.phone ?? '').toLowerCase();
    return name.includes(query) || email.includes(query) || phone.includes(query);
  });

  const totalCount = data?.unitContacts.totalCount ?? contacts.length;
  const managerCount = contacts.filter((c) => c.contactType === 'property_manager').length;
  const ownerCount = contacts.filter((c) => c.contactType === 'unit_owner').length;
  const residentCount = contacts.filter((c) => c.contactType === 'resident').length;

  function handleSaveNew(form: ContactForm): void {
    void createContact({
      variables: {
        clientCode: '',
        propertyId: 0,
        contactType: form.contactType,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email !== '' ? form.email : undefined,
        phone: form.phone !== '' ? form.phone : undefined,
      },
    }).then(() => {
      setShowAddForm(false);
    });
  }

  function handleSaveEdit(form: ContactForm): void {
    if (editingContact === null) {
      return;
    }
    void updateContact({
      variables: {
        id: editingContact.id,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email !== '' ? form.email : undefined,
        phone: form.phone !== '' ? form.phone : undefined,
        contactType: form.contactType,
        active: form.active,
      },
    }).then(() => {
      setEditingContact(null);
    });
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          Manage property managers, unit owners, and residents
        </p>
        <GlassButton
          variant="primary"
          size="md"
          onClick={() => {
            setShowAddForm(true);
            setEditingContact(null);
          }}
        >
          <Plus size={16} />
          Add Contact
        </GlassButton>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <GlassCard>
          <GlassCardContent className="flex items-center gap-3 py-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft">
              <Users size={18} className="text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-xl font-semibold text-text leading-none">
                {loading ? (
                  <SkeletonLoader className="h-6 w-10" />
                ) : (
                  <AnimatedCounter value={totalCount} />
                )}
              </p>
              <p className="mt-0.5 truncate text-xs text-text-secondary">Total</p>
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard>
          <GlassCardContent className="flex items-center gap-3 py-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-info-soft">
              <UserCog size={18} className="text-info" />
            </div>
            <div className="min-w-0">
              <p className="text-xl font-semibold text-text leading-none">
                {loading ? (
                  <SkeletonLoader className="h-6 w-10" />
                ) : (
                  <AnimatedCounter value={managerCount} />
                )}
              </p>
              <p className="mt-0.5 truncate text-xs text-text-secondary">Managers</p>
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard>
          <GlassCardContent className="flex items-center gap-3 py-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-warning-soft">
              <Contact size={18} className="text-warning" />
            </div>
            <div className="min-w-0">
              <p className="text-xl font-semibold text-text leading-none">
                {loading ? (
                  <SkeletonLoader className="h-6 w-10" />
                ) : (
                  <AnimatedCounter value={ownerCount} />
                )}
              </p>
              <p className="mt-0.5 truncate text-xs text-text-secondary">Owners</p>
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard>
          <GlassCardContent className="flex items-center gap-3 py-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-success-soft">
              <UserCheck size={18} className="text-success" />
            </div>
            <div className="min-w-0">
              <p className="text-xl font-semibold text-text leading-none">
                {loading ? (
                  <SkeletonLoader className="h-6 w-10" />
                ) : (
                  <AnimatedCounter value={residentCount} />
                )}
              </p>
              <p className="mt-0.5 truncate text-xs text-text-secondary">Residents</p>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>

      {/* Table card */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-semibold text-text">All Contacts</h2>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {/* Type filter */}
              <GlassSelect
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value as ContactTypeFilter);
                }}
                options={contactTypeOptions}
                className="w-full sm:w-44"
                aria-label="Filter by contact type"
              />
              {/* Search */}
              <div className="relative">
                <Search
                  size={14}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary"
                />
                <GlassInput
                  placeholder="Search name, email, phone…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  className="w-full pl-8 sm:w-60"
                  aria-label="Search contacts"
                />
              </div>
            </div>
          </div>
        </GlassCardHeader>

        {/* Add-new inline form */}
        {showAddForm && (
          <ContactFormPanel
            initial={emptyForm}
            onSave={handleSaveNew}
            onCancel={() => {
              setShowAddForm(false);
            }}
            saving={creating}
          />
        )}

        {loading ? (
          <div className="space-y-3 p-5">
            <SkeletonLoader className="h-10 w-full" />
            <SkeletonLoader className="h-10 w-full" />
            <SkeletonLoader className="h-10 w-full" />
          </div>
        ) : error !== undefined ? (
          <div className="py-12 text-center text-sm text-danger">
            Failed to load contacts: {error.message}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-8">
            <GlassEmptyState
              icon={Contact}
              title="No contacts found"
              description={
                search !== '' || typeFilter !== 'all'
                  ? 'Try adjusting your search or filters to find what you are looking for.'
                  : 'There are no contacts yet. Add one to get started.'
              }
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <GlassTable>
              <GlassTableHead>
                <GlassTableRow>
                  <GlassTableHeaderCell>Name</GlassTableHeaderCell>
                  <GlassTableHeaderCell>Type</GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden md:table-cell">
                    Email
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden sm:table-cell">
                    Phone
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden lg:table-cell">
                    Property
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell>Status</GlassTableHeaderCell>
                  <GlassTableHeaderCell aria-label="Actions" />
                </GlassTableRow>
              </GlassTableHead>
              <GlassTableBody>
                {filtered.map((contact) => {
                  const isEditing = editingContact?.id === contact.id;
                  return (
                    <>
                      <GlassTableRow key={contact.id}>
                        <GlassTableCell>
                          <span className="font-medium text-text">{fullName(contact)}</span>
                        </GlassTableCell>
                        <GlassTableCell>
                          <GlassBadge variant={contactTypeBadgeVariant(contact.contactType)}>
                            {formatContactType(contact.contactType)}
                          </GlassBadge>
                        </GlassTableCell>
                        <GlassTableCell className="hidden md:table-cell">
                          {contact.email !== null ? (
                            <span className="flex items-center gap-1 text-text-secondary">
                              <Mail size={12} className="shrink-0" />
                              <span className="truncate max-w-[180px]">{contact.email}</span>
                            </span>
                          ) : (
                            <span className="text-text-tertiary">—</span>
                          )}
                        </GlassTableCell>
                        <GlassTableCell className="hidden sm:table-cell">
                          {contact.phone !== null ? (
                            <span className="flex items-center gap-1 text-text-secondary">
                              <Phone size={12} className="shrink-0" />
                              {contact.phone}
                            </span>
                          ) : (
                            <span className="text-text-tertiary">—</span>
                          )}
                        </GlassTableCell>
                        <GlassTableCell className="hidden lg:table-cell text-text-secondary">
                          {contact.property?.name ?? '—'}
                        </GlassTableCell>
                        <GlassTableCell>
                          <GlassBadge variant={contact.active === 1 ? 'success' : 'default'}>
                            {contact.active === 1 ? 'Active' : 'Inactive'}
                          </GlassBadge>
                        </GlassTableCell>
                        <GlassTableCell>
                          <div className="flex items-center gap-1">
                            <GlassButton
                              variant="ghost"
                              size="sm"
                              aria-label={`Edit ${fullName(contact)}`}
                              onClick={() => {
                                setEditingContact(isEditing ? null : contact);
                                setShowAddForm(false);
                              }}
                            >
                              <Pencil size={13} />
                            </GlassButton>
                            <GlassButton
                              variant="ghost"
                              size="sm"
                              aria-label={`Delete ${fullName(contact)}`}
                              onClick={() => {
                                setDeleteTarget(contact);
                              }}
                            >
                              <Trash2 size={13} className="text-danger" />
                            </GlassButton>
                          </div>
                        </GlassTableCell>
                      </GlassTableRow>
                      {isEditing && (
                        <GlassTableRow key={`edit-${String(contact.id)}`}>
                          <GlassTableCell colSpan={7} className="p-0">
                            <ContactFormPanel
                              initial={{
                                firstName: contact.firstName,
                                lastName: contact.lastName,
                                email: contact.email ?? '',
                                phone: contact.phone ?? '',
                                contactType: contact.contactType,
                                active: contact.active ?? 1,
                              }}
                              onSave={handleSaveEdit}
                              onCancel={() => {
                                setEditingContact(null);
                              }}
                              saving={updating}
                            />
                          </GlassTableCell>
                        </GlassTableRow>
                      )}
                    </>
                  );
                })}
              </GlassTableBody>
            </GlassTable>
          </div>
        )}
      </GlassCard>

      {/* Delete confirmation */}
      <DeleteContactDialog
        contact={deleteTarget}
        open={deleteTarget !== null}
        onClose={() => {
          setDeleteTarget(null);
        }}
        onDeleted={() => {
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}
